import React from "react";
import { useTranslation } from "react-i18next";
import { Line, defaults } from "react-chartjs-2";
import "chartjs-adapter-luxon";
import { DateTime } from "luxon";
import {
  IBloodPressureData,
  IBPReading,
  IPulseReading,
  bpTimestampToMillis,
  parseBPTimestamp,
} from "../../dashboard/eventDataTypes";
import { AnalyticsChartTypes } from "../../../helpers/constants";
import { UnitChartHeader } from "./UnitChartHeader";
import AnalyticsChartContainer from "./AnalyticsChartContainer";
import { CHART_DATA_LIMIT_MINIMIZED } from "../AnalyticsViewer";
import { ChartMode } from "../AnalyticsViewerCharts";
import { timeConfigurations } from "../../../helpers/datetime";

defaults.font.family = "GothamRoundedBook";

export function UnitBloodPressureChart(props: IProps) {
  const { chartData, mode, lastUpdatedTime, isPrintable, pagination } = props;

  const { t } = useTranslation();
  const { locale } = timeConfigurations();

  const bpReadings = JSON.parse(
    JSON.stringify(chartData.bp_readings || [])
  ) as IBPReading[];
  const pulseReadings = JSON.parse(
    JSON.stringify(chartData.pulse_readings || [])
  ) as IPulseReading[];

  // Sort by timestamp
  bpReadings.sort(
    (a, b) =>
      bpTimestampToMillis(a.timestamp) - bpTimestampToMillis(b.timestamp)
  );
  pulseReadings.sort(
    (a, b) =>
      bpTimestampToMillis(a.timestamp) - bpTimestampToMillis(b.timestamp)
  );

  // Filter by pagination in maximized mode, keeping one adjacent point
  // on each side so lines connect to offscreen data
  const filterByPagination = <T extends { timestamp: string | number }>(
    data: T[]
  ): T[] => {
    if (pagination && mode === ChartMode.maximized) {
      const pStart = DateTime.fromISO(pagination.startTime, {
        zone: "utc",
      });
      const pEnd = DateTime.fromISO(pagination.endTime, {
        zone: "utc",
      });
      const spanHours = pEnd.diff(pStart, "hours").hours;
      const startTime = spanHours <= 24 ? pStart : pStart.startOf("day");
      const endTime = spanHours <= 24 ? pEnd : pEnd.endOf("day");

      let firstBefore = -1;
      let lastAfter = -1;
      const inRange: number[] = [];

      data.forEach((item, i) => {
        const date = parseBPTimestamp(item.timestamp).toUTC();
        if (date < startTime) {
          firstBefore = i;
        } else if (date > endTime) {
          if (lastAfter === -1) lastAfter = i;
        } else {
          inRange.push(i);
        }
      });

      const indices = new Set<number>(inRange);
      if (firstBefore !== -1) indices.add(firstBefore);
      if (lastAfter !== -1) indices.add(lastAfter);

      return data.filter((_, i) => indices.has(i));
    }
    return data;
  };

  const filteredBP = filterByPagination(bpReadings);
  const filteredPulse = filterByPagination(pulseReadings);

  const limitData = <T extends { timestamp: string | number }>(
    data: T[]
  ): T[] => data;

  const displayBP = limitData(filteredBP);
  const displayPulse = limitData(filteredPulse);

  const now = DateTime.now().toUTC();
  let xMin: number | undefined;
  let xMax: number | undefined;
  let xSpanHours: number | undefined;
  if (mode === ChartMode.minimized) {
    xMin = now
      .minus({ days: CHART_DATA_LIMIT_MINIMIZED - 1 })
      .startOf("day")
      .toMillis();
    xMax = now.endOf("day").toMillis();
  } else if (pagination) {
    const pStart = DateTime.fromISO(pagination.startTime, { zone: "utc" });
    const pEnd = DateTime.fromISO(pagination.endTime, { zone: "utc" });
    xSpanHours = pEnd.diff(pStart, "hours").hours;
    if (xSpanHours <= 24) {
      xMin = pStart.toMillis();
      xMax = pEnd.toMillis();
    } else {
      xMin = pStart.startOf("day").toMillis();
      xMax = pEnd.endOf("day").toMillis();
    }
  }

  // Compute y-axis range from data
  const allValues = [
    ...displayBP.map((r) => r.systolic),
    ...displayBP.map((r) => r.diastolic),
    ...displayPulse.map((r) => r.pulse),
  ];

  let yMin = 60;
  let yMax = 180;
  if (allValues.length > 0) {
    const dataMin = Math.min(...allValues);
    const dataMax = Math.max(...allValues);
    yMin = Math.floor((dataMin - 10) / 20) * 20;
    yMax = Math.ceil((dataMax + 10) / 20) * 20;
    if (yMin < 0) yMin = 0;
  }

  const systolicData = displayBP.map((r) => ({
    x: bpTimestampToMillis(r.timestamp),
    y: r.systolic,
  }));

  const diastolicData = displayBP.map((r) => ({
    x: bpTimestampToMillis(r.timestamp),
    y: r.diastolic,
  }));

  const pulseData = displayPulse.map((r) => ({
    x: bpTimestampToMillis(r.timestamp),
    y: r.pulse,
  }));

  const data = {
    datasets: [
      {
        label: t("systolic"),
        data: systolicData,
        borderColor: "#2196F3",
        backgroundColor: "#2196F3",
        borderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        fill: false,
        tension: 0.1,
      },
      {
        label: t("diastolic"),
        data: diastolicData,
        borderColor: "#000000",
        backgroundColor: "#000000",
        borderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        fill: false,
        tension: 0.1,
      },
      {
        label: t("pulse"),
        data: pulseData,
        borderColor: "#E25C4E",
        backgroundColor: "#FFFFFF",
        pointBorderColor: "#E25C4E",
        pointBackgroundColor: "#FFFFFF",
        borderWidth: 2,
        pointRadius: 5,
        pointBorderWidth: 2,
        pointHoverRadius: 7,
        fill: false,
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    plugins: {
      tooltip: {
        enabled: true,
        callbacks: {
          title: (items: any[]) => {
            if (items.length > 0) {
              const date = DateTime.fromMillis(items[0].parsed.x);
              return date.setLocale(locale).toFormat("EEE d, HH:mm");
            }
            return "";
          },
        },
      },
      legend: {
        display: true,
        position: "bottom" as const,
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          padding: 16,
        },
      },
    },
    scales: {
      x: {
        type: "time" as const,
        min: xMin,
        max: xMax,
        time: {
          unit:
            xSpanHours !== undefined && xSpanHours <= 24
              ? ("hour" as const)
              : ("day" as const),
          stepSize:
            xSpanHours !== undefined && xSpanHours > 12 && xSpanHours <= 24
              ? 4
              : 1,
          displayFormats: {
            hour: "HH:mm",
            day: "EEE d",
          },
        },
        adapters: {
          date: {
            locale: locale,
          },
        },
        ticks: {
          callback: (value: any) => {
            const parts = String(value).split(" ");
            return parts.length >= 2 ? [parts[0], parts[1]] : value;
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        min: yMin,
        max: yMax,
        ticks: {
          stepSize: 20,
        },
      },
    },
  };

  return (
    <React.Fragment>
      <UnitChartHeader
        type={AnalyticsChartTypes.blood_pressure}
        mode={mode}
        data={chartData}
        isPrintable={isPrintable}
        lastUpdatedTime={lastUpdatedTime}
      />
      <AnalyticsChartContainer isPrintable={isPrintable}>
        <Line type="line" data={data} options={options} />
      </AnalyticsChartContainer>
    </React.Fragment>
  );
}

interface IProps {
  chartData: IBloodPressureData;
  mode: ChartMode;
  lastUpdatedTime?: string;
  isPrintable: boolean;
  pagination?: { startTime: string; endTime: string };
}
